import React from "react";
import { motion } from "framer-motion";
import { companyInfo, stats, teamMembers } from "../mockData";
import {
  Award,
  Target,
  Users,
  TrendingUp,
  Building2,
  Mail,
  Linkedin,
} from "lucide-react";
import aboutImg from "../assets/about.png";
import Seo from "../components/Seo";
const About = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };
  const features = [
    {
      icon: Award,
      title: "Quality Assurance",
      description:
        "We maintain the highest standards in manufacturing and quality control to ensure consistent, superior products.",
    },
    {
      icon: Target,
      title: "Customer Focus",
      description:
        "Our commitment to understanding and meeting customer needs drives everything we do.",
    },
    {
      icon: Users,
      title: "Expert Team",
      description:
        "Our experienced team of chemists and industry experts brings decades of combined expertise.",
    },
    {
      icon: TrendingUp,
      title: "Innovation",
      description:
        "We continuously invest in research and development to bring cutting-edge solutions to market.",
    },
  ];

  return (
    <div className="min-h-screen">
      <Seo
        title="About Us - Meet Enterprise | fabric dyeing chemicals supplier"
        description="Meet Enterprise is a trusted textile dyes manufacturer and exporter supplying premium dye chemicals to Bangladesh, Vietnam, and global textile industries with consistent quality and reliability."
        keywords="textile dyes company India, textile dyes exporter Bangladesh Vietnam, dye chemicals manufacturer Gujarat"
        canonical="https://meet-enterprise.com/about"
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(135deg, rgb(238, 61, 60), rgb(36, 36, 105))",
          }}
        >
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 py-48">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white bg-opacity-20 backdrop-blur-sm rounded-2xl mb-6">
              <Building2 className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              About {companyInfo.name}
            </h1>
            <p className="text-xl md:text-2xl text-white text-opacity-90 max-w-3xl mx-auto">
              {companyInfo.tagline}
            </p>
          </motion.div>
        </div>

        {/* <div className="absolute bottom-0 left-0 right-0" style={{ lineHeight: 0 }}>
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full" style={{ display: 'block', verticalAlign: 'bottom' }}>
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div> */}
      </section>

      {/* Company Story */}
      <section className="py-20 px-4 bg-white -mt-1">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                About Meet Enterprise
              </h2>
              <p className="text-lg text-gray-600 mb-4">
                Founded in 2008, Meet Enterprise has grown into a trusted name in colour dyes and chemicals, built on a foundation of quality, innovation, and sustainability. Led by Mr. Kiran Shah, whose decades of expertise have shaped the company's strong reputation, we specialize in delivering high-performance solutions across industries including textiles, leather, paper, and more.
              </p>
              <p className="text-lg text-gray-600 mb-4">
                What makes our journey unique is the story behind it. What began as years of dedication and hard work has now evolved into a shared vision — as the next generation steps in, transforming experience into new energy.
              </p>
              <p className="text-lg text-gray-600 mb-4">
                Today, as a father-daughter duo, we carry forward a legacy rooted in trust, while continuously striving to take Meet Enterprise to new heights.
              </p>
              <p className="text-lg text-gray-600">
                With a comprehensive range of ACID, REACTIVE, DIRECT, and PIGMENT DYES, we remain committed to delivering vibrant, reliable, and tailored solutions to clients across the globe.
              </p>
            </div>

            <div className="relative">
              <img
                src={aboutImg}
                alt="Manufacturing facility"
                className="w-full h-auto rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Team Members Grid */}
      <section className="py-20 px-4 -mt-1">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">
              Our Leadership
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              The Minds Behind Our Success
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our diverse team of experts brings decades of combined experience
              in chemistry, manufacturing, and customer service
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10"
          >
            {teamMembers.map((member, index) => (
              <motion.div
                key={member.id}
                variants={itemVariants}
                whileHover={{ y: -10, transition: { duration: 0.3 } }}
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
              >
                {/* Image Container with Fixed Aspect Ratio */}
                <div className="relative w-full" style={{ paddingTop: "100%" }}>
                  <div className="absolute inset-0 overflow-hidden bg-gradient-to-br from-blue-100 to-purple-100">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-40 group-hover:opacity-60 transition-opacity"></div>

                    {/* Social Links Overlay */}
                    {/* <div className="absolute bottom-4 right-4 flex space-x-2 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                      <button className="w-10 h-10 bg-white rounded-full flex items-center justify-center hover:bg-blue-600 hover:text-white transition-all shadow-lg transform hover:scale-110">
                        <Mail className="w-5 h-5" />
                      </button>
                      <button className="w-10 h-10 bg-white rounded-full flex items-center justify-center hover:bg-blue-600 hover:text-white transition-all shadow-lg transform hover:scale-110">
                        <Linkedin className="w-5 h-5" />
                      </button>
                    </div> */}

                    {/* Position Badge */}
                    {/* <div className="absolute top-4 left-4 px-4 py-2 bg-white bg-opacity-90 backdrop-blur-sm rounded-full">
                            <p className="text-blue-600 font-bold text-sm">{member.position}</p>
                          </div> */}
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                    {member.name}
                  </h3>
                  <h5 className="text-lg font-semibold text-gray-700 mb-2">
                    {member.position}
                  </h5>
                  {/* <p className="text-gray-600 mb-4 line-clamp-3">{member.bio}</p> */}

                  {/* Expertise Tags */}
                  {/* <div className="flex flex-wrap gap-2">
                          {member.expertise.map((skill, idx) => (
                            <span
                              key={idx}
                              className="px-3 py-1 bg-gradient-to-r from-blue-50 to-purple-50 text-blue-700 text-xs font-medium rounded-full border border-blue-200 hover:border-blue-400 transition-colors"
                            >
                              {skill}
                            </span>
                          ))}
                        </div> */}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-4 bg-purple-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="text-center p-8 bg-white rounded-xl shadow-lg"
              >
                <div className="text-5xl font-bold text-purple-600 mb-2">
                  {stat.value}
                </div>
                <div className="text-xl text-gray-700">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
            Why Choose Us
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="text-center p-6 bg-gray-50 rounded-xl hover:bg-purple-50 transition-colors"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-600 text-white rounded-full mb-4">
                    <Icon className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Our Mission
              </h3>
              <p className="text-lg text-gray-600">
                To provide high-quality, innovative dye and chemical solutions
                that empower businesses to achieve excellence while maintaining
                environmental responsibility and sustainable practices.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Our Vision
              </h3>
              <p className="text-lg text-gray-600">
                To be the global leader in color dyes and chemicals, recognized
                for our commitment to quality, innovation, and customer
                satisfaction across all industries we serve.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
